import { API_BASE_URL } from '../config';
import { HsmTemplatesResponse } from '../types';

// ==================== TEMPLATES ====================

export async function fetchTemplates(accessToken: string): Promise<HsmTemplatesResponse> {
  const response = await fetch(`${API_BASE_URL}/hsm/templates`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || payload?.data?.message || 'Erro ao carregar templates');
  }

  const payload = await response.json();
  return payload.data;
}

// ==================== CREATE TEMPLATE ====================

export interface CreateTemplateRequest {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  bodyText: string;
  headerText?: string;
  headerType?: 'TEXT';
  footerText?: string;
  bodyExamples?: string[];
  headerExample?: string;
}

export interface CreateTemplateResponse {
  id: string;
  status: string;
  category: string;
}

export async function createTemplate(
  accessToken: string,
  request: CreateTemplateRequest
): Promise<CreateTemplateResponse> {
  const response = await fetch(`${API_BASE_URL}/hsm/templates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || payload?.data?.message || 'Erro ao criar template');
  }

  const payload = await response.json();
  return payload.data;
}

// ==================== UPDATE TEMPLATE ====================

export interface UpdateTemplateRequest {
  bodyText: string;
  headerText?: string;
  headerType?: 'TEXT';
  footerText?: string;
  bodyExamples?: string[];
  headerExample?: string;
}

export interface UpdateTemplateResponse {
  success: boolean;
}

export async function updateTemplate(
  accessToken: string,
  templateId: string,
  request: UpdateTemplateRequest
): Promise<UpdateTemplateResponse> {
  const response = await fetch(`${API_BASE_URL}/hsm/templates/${templateId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || payload?.data?.message || 'Erro ao atualizar template');
  }

  const payload = await response.json();
  return payload.data;
}

// ==================== TEMPLATE VARIABLES ====================

export interface TemplateVariable {
  name: string;
  placeholder: string;
  description: string;
  sampleValue: string;
}

export interface TemplateVariablesResponse {
  variables: TemplateVariable[];
}

export interface ValidateTemplateVariablesRequest {
  templateBody?: string;
  variableMapping?: string[];
}

export interface ValidateTemplateVariablesResponse {
  valid: boolean;
  errors: string[];
  detectedVariables?: string[];
}

export interface ParseTemplateVariablesRequest {
  templateBody: string;
}

export interface ParseTemplateVariablesResponse {
  variables: string[];
  placeholders: string[];
}

export async function fetchTemplateVariables(
  accessToken: string
): Promise<TemplateVariablesResponse> {
  const response = await fetch(`${API_BASE_URL}/hsm/template-variables`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar variaveis de template');
  }

  const payload = await response.json();
  return payload.data;
}

export async function validateTemplateVariables(
  accessToken: string,
  request: ValidateTemplateVariablesRequest
): Promise<ValidateTemplateVariablesResponse> {
  const response = await fetch(`${API_BASE_URL}/hsm/template-variables/validate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao validar variaveis de template');
  }

  const payload = await response.json();
  return payload.data;
}

export async function parseTemplateVariables(
  accessToken: string,
  request: ParseTemplateVariablesRequest
): Promise<ParseTemplateVariablesResponse> {
  const response = await fetch(`${API_BASE_URL}/hsm/template-variables/parse`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao analisar variaveis de template');
  }

  const payload = await response.json();
  return payload.data;
}

// ==================== TEMPLATE VARIABLE MAPPINGS ====================

export interface TemplateVariableMappingResponse {
  templateName: string;
  variableMapping: string[];
}

/**
 * Saves a template variable mapping to the backend.
 * This stores the mapping of named variables (e.g., ["Nome", "Empresa"]) for a template.
 */
export async function saveTemplateVariableMapping(
  accessToken: string,
  templateName: string,
  variableMapping: string[]
): Promise<TemplateVariableMappingResponse> {
  const response = await fetch(`${API_BASE_URL}/hsm/template-variable-mappings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify({ templateName, variableMapping })
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao salvar mapeamento de variaveis');
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Gets the variable mapping for a specific template.
 * Returns the array of variable names (e.g., ["Nome", "Empresa"]) or null if not found.
 */
export async function getTemplateVariableMapping(
  accessToken: string,
  templateName: string
): Promise<string[] | null> {
  const response = await fetch(`${API_BASE_URL}/hsm/template-variable-mappings/${encodeURIComponent(templateName)}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao obter mapeamento de variaveis');
  }

  const payload = await response.json();
  return payload.data?.variableMapping ?? null;
}

/**
 * Gets all template variable mappings.
 * Returns a Map of template names to their variable mappings.
 */
export async function getAllTemplateVariableMappings(
  accessToken: string
): Promise<Map<string, string[]>> {
  const response = await fetch(`${API_BASE_URL}/hsm/template-variable-mappings`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao obter mapeamentos de variaveis');
  }

  const payload = await response.json();
  const mappings = payload.data as TemplateVariableMappingResponse[];

  const result = new Map<string, string[]>();
  for (const mapping of mappings) {
    result.set(mapping.templateName, mapping.variableMapping);
  }
  return result;
}

// ==================== DELETE TEMPLATE ====================

/**
 * Deletes a template by name from WhatsApp.
 * Warning: This permanently deletes the template from Meta Business Suite.
 */
export async function deleteTemplate(
  accessToken: string,
  templateName: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/hsm/templates/${encodeURIComponent(templateName)}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao excluir template');
  }
}
